import { useCallback, useEffect, useRef, useState } from "react"

function csrfToken() {
  if (typeof document === "undefined") return ""
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || ""
}

// Optimistic + cancellable updater for a member's current-month KPI /
// KOI status (+, =, -). Mirrors use-rock-update: one AbortController per
// (field, rowId) so a rapid triple-tap on the same row collapses to the
// latest click, and a different row's in-flight request isn't cancelled
// by an unrelated tap.
//
// Usage on the dashboard KpiScorecard:
//   const { kpiResults, koiResults, updateResult, error, clearError } =
//     useMonthlyResultUpdate(checkInId, initialKpiResults, initialKoiResults)
//
//   updateResult("kpi", rowId, "plus")      // → PATCH kpi_result_update
//   updateResult("koi", rowId, "minus")     // → PATCH koi_result_update
//
// `field` is either "kpi" or "koi" — translated to the server's
// kpi_result_update / koi_result_update param shape. The hook carries
// its own source-of-truth arrays so the scorecard tile can reflect the
// optimistic status without waiting for a server round-trip.
export function useMonthlyResultUpdate(checkInId, initialKpi = [], initialKoi = []) {
  const [kpiResults, setKpiResults] = useState(() => Array.from(initialKpi))
  const [koiResults, setKoiResults] = useState(() => Array.from(initialKoi))
  const [error, setError] = useState(null)

  const confirmedKpiRef = useRef(Array.from(initialKpi))
  const confirmedKoiRef = useRef(Array.from(initialKoi))
  const controllersRef = useRef(new Map())
  const mountedRef = useRef(true)

  // NOTE: we deliberately do NOT useEffect-sync from initialKpi/initialKoi
  // on every render. Parent components recompute `currentMonth?.kpiResults
  // || []` inline, which produces a fresh array reference each render —
  // a naive effect would reset in-memory optimistic state on every click.
  // useState's initializer captures the first render's props; after that
  // the hook owns the state, and a full component remount (Inertia
  // navigation away + back) re-initializes from fresh props.

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      for (const c of controllersRef.current.values()) c.abort()
      controllersRef.current.clear()
    }
  }, [])

  const revert = useCallback((kind, rowId) => {
    const ref = kind === "kpi" ? confirmedKpiRef : confirmedKoiRef
    const setter = kind === "kpi" ? setKpiResults : setKoiResults
    const confirmed = ref.current.find((r) => r.id === rowId)
    setter((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, status: confirmed?.status ?? "equal" } : r)),
    )
  }, [])

  const clearError = useCallback(() => setError(null), [])

  const updateResult = useCallback(
    async (kind, rowId, value) => {
      if (!checkInId || !rowId) return false
      const key = `${kind}:${rowId}`
      const prev = controllersRef.current.get(key)
      if (prev) prev.abort()
      const ctrl = new AbortController()
      controllersRef.current.set(key, ctrl)

      // Optimistic update.
      const setter = kind === "kpi" ? setKpiResults : setKoiResults
      setter((rows) => rows.map((r) => (r.id === rowId ? { ...r, status: value } : r)))

      try {
        const body =
          kind === "kpi"
            ? { kpi_result_update: { id: rowId, value } }
            : { koi_result_update: { id: rowId, value } }

        const res = await fetch(`/app/planning/monthly_check_ins/${checkInId}`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken(),
            Accept: "application/json, text/html",
          },
          body: JSON.stringify(body),
          credentials: "same-origin",
          signal: ctrl.signal,
        })
        if (!mountedRef.current) return false

        if (!res.ok) {
          let msg = "Couldn't save. Try again."
          try {
            const data = await res.json()
            if (data?.error) msg = data.error
          } catch {
            // Non-JSON body — keep the default message.
          }
          setError(msg)
          revert(kind, rowId)
          return false
        }

        // Promote optimistic state to confirmed.
        const confRef = kind === "kpi" ? confirmedKpiRef : confirmedKoiRef
        confRef.current = confRef.current.map((r) =>
          r.id === rowId ? { ...r, status: value } : r,
        )
        return true
      } catch (err) {
        if (err?.name === "AbortError") return false
        if (!mountedRef.current) return false
        setError("Network error. Try again.")
        revert(kind, rowId)
        return false
      } finally {
        if (controllersRef.current.get(key) === ctrl) {
          controllersRef.current.delete(key)
        }
      }
    },
    [checkInId, revert],
  )

  return { kpiResults, koiResults, updateResult, error, clearError }
}
